import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { LogOut, ArrowLeft, ArrowRight, Loader2 } from 'lucide-react';
import { AppDemoProvider, useAppDemo } from '../context/AppDemoContext';
import ProgressIndicator from './ProgressIndicator';

// Import slide components
import WelcomeSlide from './slides/WelcomeSlide';
import LogoSelectionSlide from './slides/LogoSelectionSlide';
import AppAssetsSlide from './slides/AppAssetsSlide';
import MaterialThemeSlide from './slides/MaterialThemeSlide';
import AppCustomizationSlide from './slides/AppCustomizationSlide';
import BookTopicSlide from './slides/BookTopicSlide';
import VideoSelectionSlide from './slides/VideoSelectionSlide';
import ReviewSlide from './slides/ReviewSlide';
import BuildingApp from './slides/BuildingApp';
import AppPrototype from './slides/AppPrototype';

const SLIDES = [
    { id: 'welcome', label: 'Welcome' },
    { id: 'logo-selection', label: 'Logo' },
    { id: 'app-assets', label: 'App Assets' },
    { id: 'material-theme', label: 'Theme' },
    { id: 'book-selection', label: 'Book Selection' },
    { id: 'video-selection', label: 'Video Selection' },
    { id: 'review', label: 'Review' },
    { id: 'building-app', label: 'Building App' },
    { id: 'preview', label: 'App Preview' }
];

/**
 * Inner component that uses the AppDemo context
 */
const AppDemoWizardInner = ({ organization, user }) => {
    const location = useLocation();
    const navigate = useNavigate();
    const {
        currentStep,
        nextStep,
        previousStep,
        goToStep,
        isLoading,
        loadingMessage,
        setInitialData,
        fetchBrandData,
        brandDataFetched,
        websiteUrl,
        fetchAttempts,
        fetchFailed,
        primaryColor,
        secondaryColor,
        brandData,
        setOrganizationName,
        appName,
        appDescription,
        appIcon,
        appFunctionalities,
        bookTopic,
        bookTitles,
        splashVideo,
        progress,
        timeSpent,
        selectedLogo,
        selectedBanner,
        bookSearchTerm,
        videoSearchTerm,
        bookQueries,
        videoQueries,
        domainData,
        aiData,
        appIdeas,
        features,
        colorSchemes,
        marketingTips,
        contentCategories,
        brandAnalysis,
        appSuggestions,
        technicalRequirements,
        materialPalettes,
        selectedPaletteIndex,
        selectedPalette,
        savedCustomPalettes,
        appAssets
    } = useAppDemo();

    // Add local loading state for better control
    const [localLoading, setLocalLoading] = useState(false);
    const [localLoadingMessage, setLocalLoadingMessage] = useState('');
    // Add state to track fetch attempts and prevent infinite loops
    const [fetchInitiated, setFetchInitiated] = useState(false);

    // Define which slides can proceed to the next step
    const [validationStatus, setValidationStatus] = useState({});

    // Add ref to track the previous step to avoid excessive logging
    const prevStepRef = useRef(null);

    // Check for pre-populated data from registration
    useEffect(() => {
        if (location.state) {
            const { newUser, website, organizationName } = location.state;
            
            if (newUser && website) {
                // Pre-populate app data with information from registration
                setInitialData({
                    organizationName: organizationName || organization?.name,
                    websiteUrl: website,
                    isNewUser: true
                });
            }
        }
    }, [location.state, organization?.name, setInitialData]);
    
    // Use the domain from the organization or the websiteUrl from context
    const domain = organization?.website_url || organization?.website || organization?.domain || websiteUrl;
    
    // Log organization data for debugging
    useEffect(() => {
        console.log('AppDemoWizard: Organization data:', organization);
        console.log('AppDemoWizard: Extracted domain:', domain);
    }, [organization, domain]);
    
    // Fetch brand data when the component mounts
    useEffect(() => {
        const fetchData = async () => {
            // Only fetch if we haven't already fetched
            if (!brandDataFetched && !fetchInitiated) {
                setFetchInitiated(true);
                
                try {
                    // Use user ID if available (preferred) or fall back to website URL
                    if (user?.id) {
                        console.log(`AppDemoWizard: Fetching brand data for user ID: ${user.id}`);
                        // We now pass the user ID directly to ensure it's used even if
                        // the organization website URL is undefined
                        const result = await fetchBrandData(organization?.website_url || null);
                        console.log(`AppDemoWizard: Brand data fetch result: ${result ? 'success' : 'failed'}`);
                    } else if (organization?.website_url) {
                        console.log(`AppDemoWizard: Fetching brand data for website: ${organization.website_url}`);
                        const result = await fetchBrandData(organization.website_url);
                        console.log(`AppDemoWizard: Brand data fetch result: ${result ? 'success' : 'failed'}`);
                    } else {
                        console.warn('AppDemoWizard: No user ID or organization website URL available for brand data fetch');
                    }
                    
                    // If fetch failed and we haven't reached max attempts, try again after a delay
                    if (fetchAttempts < 3 && !fetchFailed) {
                        console.log(`AppDemoWizard: Retrying brand data fetch (attempt ${fetchAttempts + 1})`);
                        setTimeout(() => {
                            fetchBrandData(organization?.website_url || null);
                        }, 3000);
                    }
                } catch (error) {
                    console.error('AppDemoWizard: Error fetching brand data:', error);
                }
            }
        };
        
        fetchData();
    }, [user?.id, organization?.website_url, fetchBrandData, brandDataFetched, fetchInitiated, fetchAttempts, fetchFailed]);
    
    // Use combined loading state
    const isPageLoading = localLoading || isLoading;
    const loadingMessageToShow = localLoadingMessage || loadingMessage;

    // Check if current step can proceed to next
    // Always enable the next button as requested by the user
    const canProceed = true;

    // Register validation status from child components
    const registerValidation = useCallback((slideId, isValid) => {
        setValidationStatus(prev => {
            // Only update if the value is actually changing to prevent unnecessary re-renders
            if (prev[slideId] === isValid) {
                return prev;
            }
            return {
                ...prev,
                [slideId]: isValid
            };
        });
    }, []); // No dependencies needed as we're using the functional form of setState

    // Handle user logout
    const handleLogout = async () => {
        try {
            // Try to save state to database first if we're authenticated
            if (user?.id) {
                try {
                    console.log('Syncing app demo state to database before logout...');
                    
                    // Prepare state object for database
                    const stateToSync = {
                        step: currentStep,
                        brandData,
                        appName,
                        appDescription,
                        appIcon,
                        primaryColor,
                        secondaryColor,
                        appFunctionalities,
                        bookTopic,
                        bookTitles,
                        splashVideo,
                        websiteUrl,
                        organizationName,
                        progress,
                        timeSpent,
                        selectedLogo,
                        selectedBanner,
                        bookSearchTerm,
                        videoSearchTerm,
                        bookQueries,
                        videoQueries,
                        domainData,
                        aiData,
                        appIdeas,
                        features, 
                        colorSchemes,
                        marketingTips,
                        contentCategories,
                        brandAnalysis,
                        appSuggestions,
                        technicalRequirements,
                        materialPalettes,
                        selectedPaletteIndex,
                        selectedPalette,
                        savedCustomPalettes,
                        appAssets,
                        lastSynced: new Date().toISOString()
                    };
                    
                    // Send to database
                    const response = await fetch('/api/app-demo-state/sync', {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json'
                        },
                        credentials: 'include',
                        body: JSON.stringify({
                            state: stateToSync,
                            organizationId: user?.organization_id || null
                        })
                    });
                    
                    console.log('State synced to database before logout', response.ok);
                } catch (error) {
                    console.error('Error syncing state to database before logout:', error);
                }
            }
            
            // Make a server-side logout request
            const response = await fetch('/api/auth/logout', {
                method: 'POST',
                credentials: 'include'
            });
            
            // Clear user data from localStorage
            localStorage.removeItem('userData');
            
            // Also clear app demo state to prevent persistence between users
            localStorage.removeItem('appDemoState');
            
            // Clear saved material palettes to prevent accumulation across user sessions
            localStorage.removeItem('savedMaterialPalettes');
            
            // Redirect to home page using window.location for a full page reload
            window.location.href = '/';
        } catch (error) {
            console.error('Logout failed:', error);
            
            // Even if server logout fails, clear client-side state and redirect
            localStorage.removeItem('userData');
            localStorage.removeItem('appDemoState');
            localStorage.removeItem('savedMaterialPalettes');
            window.location.href = '/';
        }
    };

    // Render the current step
    const renderCurrentSlide = () => {
        // Only log in development mode and only once per slide change
        if (process.env.NODE_ENV === 'development') {
            // Using a ref to log only when the slide changes
            if (prevStepRef.current !== currentStep) {
                console.log(`🎬 Rendering slide for step ${currentStep}: ${SLIDES[currentStep]?.id}`);
                console.log(`🏢 Organization data being passed to slide:`, {
                    id: organization?.id,
                    name: organization?.name,
                    domain: organization?.domain || organization?.website_url,
                    website: organization?.website || organization?.website_url,
                    website_url: organization?.website_url
                });
                // Update the ref to prevent logging again until the slide changes
                prevStepRef.current = currentStep;
            }
        }
        
        // Extract organization ID from URL if not available in context
        const params = new URLSearchParams(window.location.search);
        const urlOrgId = params.get('organizationId') || params.get('org');
        
        // Ensure organization data is properly formatted for slides
        const enhancedOrg = organization ? {
            ...organization,
            // Ensure domain is available (derived from website_url if needed)
            domain: organization.domain || organization.website_url,
            // Ensure ID is available even if organization object doesn't have it
            id: organization.id || urlOrgId
        } : urlOrgId ? { id: urlOrgId } : null;

        // For debugging - log the enhanced organization with ID from URL if needed
        if (urlOrgId && !organization?.id) {
            console.log('AppDemoWizard: Using organization ID from URL:', urlOrgId);
        }
        
        switch (SLIDES[currentStep].id) {
            case 'welcome':
                return <WelcomeSlide registerValidation={registerValidation} organization={enhancedOrg} />;
            case 'logo-selection':
                return <LogoSelectionSlide registerValidation={registerValidation} organization={enhancedOrg} />;
            case 'app-assets':
                return <AppAssetsSlide registerValidation={registerValidation} organization={enhancedOrg} user={user} />;
            case 'material-theme':
                return <MaterialThemeSlide registerValidation={registerValidation} organization={enhancedOrg} />;
            case 'book-selection':
                return <BookTopicSlide 
                    organization={enhancedOrg}
                    registerValidation={registerValidation} 
                />;
            case 'video-selection':
                return <VideoSelectionSlide 
                    organization={enhancedOrg}
                    userId={user?.id} 
                    registerValidation={registerValidation} 
                />;
            case 'review':
                return <ReviewSlide registerValidation={registerValidation} />;
            case 'building-app':
                return <BuildingApp />;
            case 'preview':
                return <AppPrototype 
                    organization={enhancedOrg}
                />;
            default:
                return <div>Unknown step</div>;
        }
    };

    // Get company logo using Clearbit if organization has a website URL
    const companyLogo = organization?.website_url 
        ? `https://logo.clearbit.com/${organization.website_url.replace(/^https?:\/\//, '').replace(/\/$/, '')}` 
        : null;

    return (
        <div className="min-h-screen h-screen bg-green-800 text-white flex flex-col">
            {/* Main container with fixed height */}
            <div className="flex-1 flex flex-col p-2 sm:p-3">
                {/* Main "glassy" container with fixed height and width */}
                <div className="w-full max-w-[1400px] mx-auto h-[90vh] bg-white/10 backdrop-blur-md rounded-xl border border-white/20 shadow-xl flex flex-col overflow-hidden">
                    {/* Progress indicator at the top of the glassy container */}
                    <div className="py-2 px-4 border-b border-white/10 bg-green-700/20">
                        <ProgressIndicator 
                            steps={SLIDES.map(s => s.label)} 
                            currentStep={currentStep}
                            onStepClick={goToStep}
                            className="w-full"
                            minimal={true}
                        />
                    </div>
                    
                    {/* Main content area with scrolling */}
                    <div className="flex-1 overflow-y-auto p-4 md:p-6">
                        {isPageLoading ? (
                            <div className="flex flex-col items-center justify-center py-12">
                                <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-yellow-300 mb-4"></div>
                                <p className="text-white text-lg">{loadingMessageToShow || 'Loading...'}</p>
                            </div>
                        ) : (
                            <div className="min-h-full flex flex-col">
                                {renderCurrentSlide()}
                            </div>
                        )}
                    </div>
                    
                    {/* Fixed footer with navigation and user info */}
                    {currentStep < 6 && (
                        <div className="p-3 sm:p-4 border-t border-white/10 flex items-center justify-between bg-green-700/20">
                            {/* Back button on the left */}
                            <div>
                                <button 
                                    type="button"
                                    onClick={previousStep}
                                    disabled={currentStep === 0}
                                    className="inline-flex items-center px-3 py-2 rounded-md
                                        transition-colors duration-200 text-sm font-medium
                                        disabled:opacity-40 disabled:cursor-not-allowed
                                        text-white bg-gray-700/50 hover:bg-gray-700/70 
                                        focus:outline-none focus:bg-gray-700/90"
                                >
                                    <ArrowLeft className="w-4 h-4 mr-1" />
                                    <span className="hidden xs:inline">Back</span>
                                </button>
                            </div>
                            
                            {/* User info in the middle */}
                            <div className="flex items-center space-x-3">
                                {companyLogo && (
                                    <img src={companyLogo} alt="Organization logo" className="h-6 w-6 rounded-full" />
                                )}
                                <DataLoader isLoading={!user?.email}>
                                    <span className="text-white text-sm hidden sm:inline">{user?.email}</span>
                                </DataLoader>
                                <button
                                    onClick={handleLogout}
                                    className="text-white px-2 py-1 bg-gray-700/40 hover:bg-gray-700/70 rounded-md text-sm flex items-center"
                                >
                                    <LogOut className="w-4 h-4" />
                                    <span className="hidden sm:inline ml-1">Logout</span>
                                </button>
                            </div>
                            
                            {/* Next button on the right */}
                            <div>
                                <button
                                    type="button"
                                    onClick={nextStep}
                                    disabled={!canProceed}
                                    className="inline-flex items-center px-4 py-2 rounded-md
                                        bg-yellow-500 text-green-900 shadow-sm hover:bg-yellow-400
                                        transition-colors duration-200 text-sm font-semibold
                                        disabled:opacity-40 disabled:cursor-not-allowed
                                        focus:outline-none focus:ring-2 focus:ring-yellow-300/50 focus:ring-offset-2 focus:ring-offset-green-800"
                                >
                                    <span>{currentStep === 5 ? 'Complete' : 'Next'}</span>
                                    {currentStep < 5 && <ArrowRight className="w-4 h-4 ml-1" />}
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

/**
 * Main App Demo Wizard component with context provider
 */
const AppDemoWizard = (props) => {
    // If we have initialData (e.g. from URL params), we'd apply it here
    const initialData = props.initialData || {};
    
    return (
        <AppDemoWizardInner {...props} />
    );
};

// Add a loading component for missing data
const DataLoader = ({ isLoading, children, fallback = null, showSpinner = true }) => {
    if (isLoading) {
        return (
            <div className="flex items-center space-x-2 text-white/70">
                {showSpinner && <Loader2 className="w-4 h-4 animate-spin" />}
                <span className="text-sm">Loading data...</span>
            </div>
        );
    }
    
    // If we have children, render them
    if (children) {
        return children;
    }
    
    // If we have a fallback, render it
    if (fallback) {
        return fallback;
    }
    
    // Default fallback
    return (
        <div className="flex items-center space-x-2 text-white/70">
            <span className="text-sm">No data available</span>
        </div>
    );
};

export default AppDemoWizard; 